import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import InstallEdujac from "@/components/InstallEdujac";
import { homeFor, useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

const REMEMBER_KEY = "eduspark_remembered_email";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [rememberIdentifier, setRememberIdentifier] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [loading, setLoading] = useState(false);

  const loginError = (err) => {
    if (err?.response) {
      toast.error(formatErr(err, t("auth.loginFailed")));
    } else {
      toast.error(t("auth.serverUnavailable"));
    }
  };

  const loginWithRetry = async (e, p) => {
    try {
      return await login(e, p);
    } catch (err) {
      if (!err?.response || err.response.status >= 500) {
        await new Promise((r) => setTimeout(r, 3000));
        return await login(e, p);
      }
      throw err;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await loginWithRetry(identifier.trim(), password);
      if (rememberIdentifier) {
        localStorage.setItem(REMEMBER_KEY, identifier.trim());
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      // A full replacement guarantees that every role dashboard starts with
      // the newly stored token and user state; users no longer need to press F5.
      window.location.replace(homeFor(user));
    } catch (err) {
      loginError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7] grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8" data-testid="login-brand">
          <img src="/edujac-mark.png" alt="EDUJAC" className="h-16 w-16 object-contain" />
          <span className="text-center">
            <span className="block font-heading font-extrabold text-[26px] leading-none tracking-wide text-[#00194E]">EDUJAC</span>
            <span className="block text-[11px] font-semibold tracking-[0.08em] text-[#119687] mt-1.5">{t("auth.platformSubtitle")}</span>
          </span>
        </Link>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="login-form">
          <h1 className="font-heading text-2xl font-bold">{t("auth.welcomeBack")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("auth.loginSubtitle")}</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="identifier">Email or Mobile Number</Label>
              <Input id="identifier" data-testid="login-identifier" type="text" inputMode="text" autoComplete="username" required className="mt-1.5" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@school.in or 9876543210" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("common.password")}</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-brand-coralDark hover:underline">{t("auth.forgotPassword")}</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" required className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-identifier"
                data-testid="remember-identifier-checkbox"
                checked={rememberIdentifier}
                onCheckedChange={(v) => setRememberIdentifier(v === true)}
              />
              <Label htmlFor="remember-identifier" className="text-sm font-normal text-slate-600 cursor-pointer">Remember email or mobile</Label>
            </div>
            <Button
              type="submit"
              data-testid="login-form-submit-button"
              disabled={loading}
              className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold py-5 transition-transform duration-300 active:scale-95"
            >
              {loading ? t("auth.loggingIn") : t("auth.login")}
            </Button>
            <InstallEdujac />
          </div>
        </form>
        <p className="text-center text-xs text-slate-400 mt-6">
          {t("auth.newToEdujac")} <Link to="/" className="font-bold text-brand-coralDark hover:underline" data-testid="login-register-link">{t("auth.requestDemo")}</Link>
        </p>
      </div>
    </div>
  );
}
